import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Tabs, message } from 'antd';
import { StyledButton } from './Styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppDispatch, RootState } from '../../../redux/store';
import {
  fetchMerchantData,
  fetchStores,
  fetchEarnings,
  exportEarningsCSV,
  setSelectedStore,
  setSelectedDateRange,
  clearError,
} from '../../../redux/MerchantEarnings';
import { useAuth } from '../../../hooks/useAuth';
import { useTheme } from '../../../context/ThemeContext';
import { ThemeProvider } from 'styled-components';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import { ConfigProvider } from 'antd';
import moment from 'moment';
import {
  Container,
  ContentWrapper,
  HeaderContainer,
  HeaderLeft,
  HeaderRight,
  TitleSection,
  Title,
  Subtitle,
  StyledCard,
  StyledTabs,
  ErrorContainer,
  ErrorIcon,
  NoDataContainer,
  NoDataIcon,
  ActionButtons,
  ErrorTitle,
  ErrorSubtitle,
  NoDataTitle,
  NoDataReasonsList,
  NoDataReasonItem,
  TabIcon,
} from './Styles';
import {
  ICONS,
  TAB_KEYS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  NO_DATA_REASONS,
} from './constants';
import SummaryCards from './SummaryCards';
import PlatformBreakdown from './PlatformBreakdown';
import FulfillmentBreakdown from './FulfillmentBreakdown';
import FeeCategories from './FeeCategories';
import FeeCategoryModal from './FeeCategoryModal';
import Filters from './Filters';
import { FeeCategoryData } from './types';

const MerchantEarnings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const storeId = localStorage.getItem('storeId');
  const theme = { mode: isDarkMode ? 'dark' : 'light' } as const;
  const antdTheme = getAntdThemeConfig(isDarkMode);

  const {
    earningsData,
    merchantData,
    stores,
    selectedStore,
    selectedDateRange,
    loading,
    error,
  } = useSelector((state: RootState) => state.merchantEarnings);

  const [activeTab, setActiveTab] = useState<string>(TAB_KEYS.PLATFORM);
  const [feeCategoryModalVisible, setFeeCategoryModalVisible] = useState(false);
  const [selectedFeeCategory, setSelectedFeeCategory] = useState<FeeCategoryData | null>(null);

  // Initialize data on mount
  useEffect(() => {
    const initializeData = async () => {
      await Promise.all([
        dispatch(fetchMerchantData()),
        dispatch(fetchStores({ merchantId: merchantData?.id, role: user?.role })),
      ]);
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Set initial store from auth context
  useEffect(() => {
    if (storeId && !selectedStore) {
      dispatch(setSelectedStore(storeId));
    } else if (stores.length > 0 && !selectedStore) {
      dispatch(setSelectedStore(stores[0].id));
    }
  }, [storeId, stores, selectedStore, dispatch]);

  // Fetch earnings when dependencies change
  useEffect(() => {
    if (merchantData?.id && selectedStore && selectedDateRange.startDate && selectedDateRange.endDate) {
      dispatch(
        fetchEarnings({
          merchantId: merchantData.id,
          startDate: selectedDateRange.startDate,
          endDate: selectedDateRange.endDate,
          store: selectedStore,
        })
      );
    }
  }, [merchantData?.id, selectedStore, selectedDateRange, dispatch]);

  const handleDateRangeChange = (range: { startDate: moment.Moment | null; endDate: moment.Moment | null }) => {
    dispatch(setSelectedDateRange(range));
  };

  const handleStoreChange = (storeId: string | null) => {
    dispatch(setSelectedStore(storeId));
  };

  const handleApplyFilters = () => {
    if (!merchantData?.id) {
      message.error(ERROR_MESSAGES.NO_MERCHANT_DATA);
      return;
    }

    if (!selectedStore) {
      message.error(ERROR_MESSAGES.NO_STORE);
      return;
    }

    if (!selectedDateRange.startDate || !selectedDateRange.endDate) {
      message.error('Please select a date range');
      return;
    }

    dispatch(
      fetchEarnings({
        merchantId: merchantData.id,
        startDate: selectedDateRange.startDate,
        endDate: selectedDateRange.endDate,
        store: selectedStore,
      })
    );
  };

  const handleExportCSV = async () => {
    if (!merchantData?.id) {
      message.error(ERROR_MESSAGES.NO_MERCHANT_DATA);
      return;
    }

    if (!selectedStore) {
      message.error(ERROR_MESSAGES.NO_STORE);
      return;
    }

    try {
      const csvData = await dispatch(
        exportEarningsCSV({
          merchantId: merchantData.id,
          startDate: selectedDateRange.startDate || moment().subtract(1, 'day'),
          endDate: selectedDateRange.endDate || moment(),
          store: selectedStore,
        })
      ).unwrap();

      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `earnings-${moment().format('YYYY-MM-DD')}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);

      message.success(SUCCESS_MESSAGES.EXPORT_SUCCESS);
    } catch (error: any) {
      message.error(error || ERROR_MESSAGES.EXPORT_FAILED);
    }
  };

  const handleViewFeeCategoryDetails = (category: string, data: FeeCategoryData['data']) => {
    setSelectedFeeCategory({ category, data });
    setFeeCategoryModalVisible(true);
  };

  const handleCloseFeeCategoryModal = () => {
    setFeeCategoryModalVisible(false);
    setSelectedFeeCategory(null);
  };

  const handleRetry = () => {
    dispatch(clearError());
    if (merchantData?.id && selectedStore) {
      handleApplyFilters();
    } else {
      dispatch(fetchMerchantData());
      dispatch(fetchStores({ merchantId: merchantData?.id, role: user?.role }));
    }
  };


  // Error state
  if (error && !earningsData) {
    return (
      <ConfigProvider theme={antdTheme}>
        <ThemeProvider theme={theme}>
          <Container>
            <ContentWrapper>
              <HeaderContainer>
                <HeaderLeft>
                  <StyledButton
                    icon={<FontAwesomeIcon icon={ICONS.arrowLeft} />}
                    onClick={() => navigate(-1)}
                    type="text"
                  />
                  <TitleSection>
                    <Title>Merchant Earnings</Title>
                    <Subtitle>Detailed earnings breakdown with fee analysis</Subtitle>
                  </TitleSection>
                </HeaderLeft>
              </HeaderContainer>

              <StyledCard>
                <ErrorContainer>
                  <ErrorIcon>
                    <FontAwesomeIcon icon={ICONS.alertCircle} size="2x" />
                  </ErrorIcon>
                  <div>
                    <ErrorTitle>Error Loading Data</ErrorTitle>
                    <Subtitle>{error}</Subtitle>
                    {stores.length === 0 && (
                      <ErrorSubtitle>
                        {ERROR_MESSAGES.NO_STORES_FOUND}
                      </ErrorSubtitle>
                    )}
                  </div>
                  <ActionButtons>
                    <StyledButton type="primary" icon={<FontAwesomeIcon icon={ICONS.refresh} />} onClick={handleRetry}>
                      Try Again
                    </StyledButton>
                    <StyledButton icon={<FontAwesomeIcon icon={ICONS.arrowLeft} />} onClick={() => navigate(-1)}>
                      Go Back
                    </StyledButton>
                  </ActionButtons>
                </ErrorContainer>
              </StyledCard>
            </ContentWrapper>
          </Container>
        </ThemeProvider>
      </ConfigProvider>
    );
  }

  // No data state
  if (!earningsData) {
    return (
      <ConfigProvider theme={antdTheme}>
        <ThemeProvider theme={theme}>
          <Container>
            <ContentWrapper>
              <HeaderContainer>
                <HeaderLeft>
                  <StyledButton
                    icon={<FontAwesomeIcon icon={ICONS.arrowLeft} />}
                    onClick={() => navigate(-1)}
                    type="text"
                  />
                  <TitleSection>
                    <Title>Merchant Earnings</Title>
                    <Subtitle>Detailed earnings breakdown with fee analysis</Subtitle>
                  </TitleSection>
                </HeaderLeft>
              </HeaderContainer>

              <Filters
                selectedDateRange={selectedDateRange}
                onDateRangeChange={handleDateRangeChange}
                selectedStore={selectedStore}
                onStoreChange={handleStoreChange}
                merchantId={merchantData?.id}
                role={user?.role}
                onApplyFilters={handleApplyFilters}
                loading={loading}
              />

              <StyledCard>
                <NoDataContainer>
                  <NoDataIcon>
                    <FontAwesomeIcon icon={ICONS.alertCircle} size="2x" />
                  </NoDataIcon>
                  <div>
                    <NoDataTitle>No Earnings Data Available</NoDataTitle>
                    <Subtitle>
                      We couldn't find any earnings data for the selected period and filters. This could be because:
                    </Subtitle>
                  </div>
                  <NoDataReasonsList>
                    {NO_DATA_REASONS.map((reason, index) => (
                      <NoDataReasonItem key={index}>
                        <FontAwesomeIcon icon={ICONS.minus} style={{ color: isDarkMode ? '#8c8c8c' : '#8c8c8c' }} />
                        <Subtitle>{reason}</Subtitle>
                      </NoDataReasonItem>
                    ))}
                  </NoDataReasonsList>
                  <ActionButtons>
                    <StyledButton
                      type="primary"
                      icon={<FontAwesomeIcon icon={ICONS.filter} />}
                      onClick={handleApplyFilters}
                      loading={loading}
                    >
                      Try Again
                    </StyledButton>
                    <StyledButton icon={<FontAwesomeIcon icon={ICONS.arrowLeft} />} onClick={() => navigate(-1)}>
                      Go Back
                    </StyledButton>
                  </ActionButtons>
                </NoDataContainer>
              </StyledCard>
            </ContentWrapper>
          </Container>
        </ThemeProvider>
      </ConfigProvider>
    );
  }

  // Main content
  return (
    <ConfigProvider theme={antdTheme}>
      <ThemeProvider theme={theme}>
        <Container>
          <ContentWrapper>
            <HeaderContainer>
              <HeaderLeft>
                <StyledButton
                  icon={<FontAwesomeIcon icon={ICONS.arrowLeft} />}
                  onClick={() => navigate(-1)}
                  type="text"
                />
                <TitleSection>
                  <Title>Merchant Earnings</Title>
                  <Subtitle>
                    {earningsData.merchantName} • {earningsData.period?.startDate} to {earningsData.period?.endDate}
                  </Subtitle>
                </TitleSection>
              </HeaderLeft>
              <HeaderRight>
                <StyledButton
                  icon={<FontAwesomeIcon icon={ICONS.download} />}
                  onClick={handleExportCSV}
                  type="default"
                >
                  Export CSV
                </StyledButton>
                <StyledButton
                  icon={<FontAwesomeIcon icon={ICONS.refresh} />}
                  onClick={handleApplyFilters}
                  loading={loading}
                  type="primary"
                >
                  Refresh
                </StyledButton>
              </HeaderRight>
            </HeaderContainer>

            <Filters
              selectedDateRange={selectedDateRange}
              onDateRangeChange={handleDateRangeChange}
              selectedStore={selectedStore}
              onStoreChange={handleStoreChange}
              merchantId={merchantData?.id}
              role={user?.role}
              onApplyFilters={handleApplyFilters}
              loading={loading}
            />

            {loading && !earningsData ? (
              <>
                <SummaryCards summary={null} loading={true} />
                <StyledTabs activeKey={activeTab} onChange={setActiveTab} type="card">
                  <Tabs.TabPane
                    tab={
                      <span>
                        <TabIcon icon={ICONS.desktop} />
                        Platform Breakdown
                      </span>
                    }
                    key={TAB_KEYS.PLATFORM}
                  >
                    <PlatformBreakdown platformBreakdown={null} loading={true} />
                  </Tabs.TabPane>
                  <Tabs.TabPane
                    tab={
                      <span>
                        <TabIcon icon={ICONS.truck} />
                        Fulfillment Breakdown
                      </span>
                    }
                    key={TAB_KEYS.FULFILLMENT}
                  >
                    <FulfillmentBreakdown fulfillmentBreakdown={null} loading={true} />
                  </Tabs.TabPane>
                  <Tabs.TabPane
                    tab={
                      <span>
                        <TabIcon icon={ICONS.percent} />
                        Fee Categories
                      </span>
                    }
                    key={TAB_KEYS.FEES}
                  >
                    <FeeCategories feeCategories={null} onViewDetails={handleViewFeeCategoryDetails} loading={true} />
                  </Tabs.TabPane>
                </StyledTabs>
              </>
            ) : earningsData ? (
              <>
                <SummaryCards summary={earningsData.summary} loading={false} />
                <StyledTabs activeKey={activeTab} onChange={setActiveTab} type="card">
                  <Tabs.TabPane
                    tab={
                      <span>
                        <TabIcon icon={ICONS.desktop} />
                        Platform Breakdown
                      </span>
                    }
                    key={TAB_KEYS.PLATFORM}
                  >
                    <PlatformBreakdown platformBreakdown={earningsData.platformBreakdown} loading={false} />
                  </Tabs.TabPane>
                  <Tabs.TabPane
                    tab={
                      <span>
                        <TabIcon icon={ICONS.truck} />
                        Fulfillment Breakdown
                      </span>
                    }
                    key={TAB_KEYS.FULFILLMENT}
                  >
                    <FulfillmentBreakdown fulfillmentBreakdown={earningsData.fulfillmentBreakdown} loading={false} />
                  </Tabs.TabPane>
                  <Tabs.TabPane
                    tab={
                      <span>
                        <TabIcon icon={ICONS.percent} />
                        Fee Categories
                      </span>
                    }
                    key={TAB_KEYS.FEES}
                  >
                    <FeeCategories
                      feeCategories={earningsData.feeCategories}
                      onViewDetails={handleViewFeeCategoryDetails}
                      loading={false}
                    />
                  </Tabs.TabPane>
                </StyledTabs>
              </>
            ) : null}
          </ContentWrapper>

          <FeeCategoryModal
            visible={feeCategoryModalVisible}
            onClose={handleCloseFeeCategoryModal}
            categoryData={selectedFeeCategory}
          />
        </Container>
      </ThemeProvider>
    </ConfigProvider>
  );
};

export default MerchantEarnings;
